import { Injectable } from '@angular/core';
import { DateTime } from '@packages/datetime-picker';
import { TuiDay, TuiDayRange } from '@taiga-ui/cdk';
import { FirstLetterCapitalizePipe } from '../../pipes/firstLetterCapitalize.pipe';

@Injectable({
  providedIn: 'root',
})
export class DateService {
  private firstLetterCapitalizePipe = new FirstLetterCapitalizePipe();

  isFutureMonthColumn(index: number, referenceDate = new Date()): boolean {
    const currentMonthIndex = referenceDate.getMonth();

    return index >= 0 && index > currentMonthIndex && index < 12;
  }

  isMonthColumnHidden(index: number, dateRange?: string, referenceDate = new Date()): boolean {
    if (index < 0 || index > 11) {
      return false;
    }

    const selectedMonths = this.getSelectedMonths(dateRange);

    if (!selectedMonths) {
      return this.isFutureMonthColumn(index, referenceDate);
    }

    const firstMatchingMonth = selectedMonths.startAbsoluteMonth + ((index - selectedMonths.startMonth + 12) % 12);

    if (firstMatchingMonth > selectedMonths.endAbsoluteMonth) {
      return true;
    }

    const currentAbsoluteMonth = referenceDate.getFullYear() * 12 + referenceDate.getMonth();
    return firstMatchingMonth > currentAbsoluteMonth;
  }

  private getSelectedMonths(value?: string): { startMonth: number; startAbsoluteMonth: number; endAbsoluteMonth: number } | null {
    if (!value) {
      return null;
    }

    const [startValue, endValue] = value.split('|');
    const start = this.parseFilterMonth(startValue);
    const end = this.parseFilterMonth(endValue);

    if (!start || !end) {
      return null;
    }

    const startAbsoluteMonth = start.year * 12 + start.month;
    const endAbsoluteMonth = end.year * 12 + end.month;

    return endAbsoluteMonth >= startAbsoluteMonth ? { startMonth: start.month, startAbsoluteMonth, endAbsoluteMonth } : null;
  }

  private parseFilterMonth(value?: string): { year: number; month: number } | null {
    const date = value?.trim();
    const frenchDate = date?.match(/^\d{1,2}\/(\d{1,2})\/(\d{4})/);
    const isoDate = date?.match(/^(\d{4})-(\d{2})-\d{2}/);
    const year = frenchDate ? Number(frenchDate[2]) : isoDate ? Number(isoDate[1]) : 0;
    const month = frenchDate ? Number(frenchDate[1]) - 1 : isoDate ? Number(isoDate[2]) - 1 : -1;

    return year > 0 && month >= 0 && month < 12 ? { year, month } : null;
  }

  isPreviousPeriodRow(label: string): boolean {
    return /^(?:% Rem )?N-\d+$/.test(label.trim());
  }

  shouldDisplayMonthlyValue(index: number, rowLabel: string, referenceDate = new Date()): boolean {
    return !this.isFutureMonthColumn(index, referenceDate) || this.isPreviousPeriodRow(rowLabel);
  }

  shouldDisplayMonthlyCellValue(index: number, rowLabel: string, value: number | null | undefined, referenceDate = new Date()): boolean {
    if (!this.shouldDisplayMonthlyValue(index, rowLabel, referenceDate)) {
      return false;
    }

    return !this.isFutureMonthColumn(index, referenceDate) || (value != null && value !== 0);
  }

  get method() {
    return {
      getStartTodayDate: this.getStartTodayDate(),
      getEndTodayDate: this.getEndTodayDate(),
      getStartYesterdayDate: this.getStartYesterdayDate(),
      getEndYesterdayDate: this.getEndYesterdayDate(),
      getStartWeekDate: this.getStartWeekDate(),
      getEndWeekDate: this.getEndWeekDate(),
      getLastMonthStartDate: this.getLastMonthStartDate(),
      getLastMonthEndDate: this.getLastMonthEndDate(),
      getStartMonthDate: this.getStartMonthDate(),
      getEndMonthDate: this.getEndMonthDate(),
      getStartTrimesterDate: this.getStartTrimesterDate(),
      getEndTrimesterDate: this.getEndTrimesterDate(),
      getStartYearDate: this.getStartYearDate(),
      getEndYearDate: this.getEndYearDate(),
      get1970Date: this.get1970Date(),
      getDateNow: this.getDateNow(),
      getCurrentMonthAndYear: this.getCurrentMonthAndYear(),
      getCurrentYear: this.getCurrentYear(),
    };
  }

  constructor() {}

  getStartTodayDate() {
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Utilisez toLocaleString avec l'option timeZone
    const todayLocal = todayStart.toLocaleString(undefined, { timeZone });

    return todayLocal;
  }

  getEndTodayDate() {
    const today = new Date();
    const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 0);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Utilisez toLocaleString avec l'option timeZone
    const todayLocal = todayEnd.toLocaleString(undefined, { timeZone });

    return todayLocal;
  }

  /**
   * @description
   * Return the date of the previous day
   * @returns {string} local date string of the date
   * @example '01/01/2024 00:00:00'
   */
  getStartYesterdayDate() {
    const today = new Date();
    const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1, 0, 0, 0);
    // Obtenez le nom du fuseau horaire local
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Utilisez toLocaleString avec l'option timeZone
    const yesterdayLocal = yesterday.toLocaleString(undefined, { timeZone });

    return yesterdayLocal;
  }

  getEndYesterdayDate() {
    const today = new Date();
    const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1, 23, 59, 59);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Utilisez toLocaleString avec l'option timeZone
    const yesterdayLocal = yesterday.toLocaleString(undefined, { timeZone });

    return yesterdayLocal;
  }

  /**
   * @description
   * Return the date of the first day of the current week
   * @returns {string} local date string of the date
   * @example '01/01/2024 00:00:00'
   */
  getStartWeekDate() {
    let date = new Date();
    let day = date.getDay();
    let diff = date.getDate() - day + (day === 0 ? -6 : 1);
    // Créez une nouvelle date avec le fuseau horaire local
    let startOfWeek = new Date(date.getFullYear(), date.getMonth(), diff, 0, 0, 0);

    // Obtenez le nom du fuseau horaire local
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Utilisez toLocaleString avec l'option timeZone
    const startOfWeekLocal = startOfWeek.toLocaleString(undefined, { timeZone });

    return startOfWeekLocal;
  }
  /**
   * @description
   * Return the date of the last day of the current week
   * @returns {string} local date string of the date
   * @example '01/01/2024 23:59:59'
   */
  getEndWeekDate() {
    let date = new Date();
    let day = date.getDay();
    let diff = date.getDate() - day + (day === 0 ? 0 : 7);
    // Créez une nouvelle date avec le fuseau horaire local
    let endOfWeek = new Date(date.getFullYear(), date.getMonth(), diff, 23, 59, 59);

    // Obtenez le nom du fuseau horaire local
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Utilisez toLocaleString avec l'option timeZone
    const endOfWeekLocal = endOfWeek.toLocaleString(undefined, { timeZone });

    return endOfWeekLocal;
  }

  /**
   * @description
   * Return the date of the first day of the last month
   * @returns {string} local date string of the date
   * @example '01/01/2024 00:00:00'
   */
  getLastMonthStartDate() {
    let date = new Date();
    // Créez une nouvelle date avec le fuseau horaire local
    let startOfMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1, 0, 0, 0);

    // Obtenez le nom du fuseau horaire local
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Utilisez toLocaleString avec l'option timeZone
    const startOfMonthLocal = startOfMonth.toLocaleString(undefined, { timeZone });

    return startOfMonthLocal;
  }

  /**
   * @description
   * Return the date of the last day of the last month
   * @returns {string} local date string of the date
   * @example '31/01/2024 23:59:59'
   */
  getLastMonthEndDate() {
    let date = new Date();
    // Créez une nouvelle date avec le fuseau horaire local
    let endOfMonth = new Date(date.getFullYear(), date.getMonth(), 0, 23, 59, 59);

    // Obtenez le nom du fuseau horaire local
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Utilisez toLocaleString avec l'option timeZone
    const endOfMonthLocal = endOfMonth.toLocaleString(undefined, { timeZone });

    return endOfMonthLocal;
  }

  /**
   * @description
   * Return the date of the first day of the current month
   * @returns {string} local date string of the date
   * @example '01/01/2024 00:00:00'
   */
  getStartMonthDate() {
    let date = new Date();
    // Créez une nouvelle date avec le fuseau horaire local
    let startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0);

    // Obtenez le nom du fuseau horaire local
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Utilisez toLocaleString avec l'option timeZone
    const startOfMonthLocal = startOfMonth.toLocaleString(undefined, { timeZone });

    return startOfMonthLocal;
  }
  /**
   * @description
   * Return the date of the first day of the current month with offset
   * @returns {string} local date string of the date
   * @example '01/01/2024 00:00:00'
   */
  getStartMonthWithOffsetDate(offset: number) {
    let date = new Date();
    // Créez une nouvelle date avec le fuseau horaire local
    let startOfMonth = new Date(date.getFullYear(), date.getMonth() + offset, 1, 0, 0, 0);

    // Obtenez le nom du fuseau horaire local
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Utilisez toLocaleString avec l'option timeZone
    const startOfMonthLocal = startOfMonth.toLocaleString(undefined, { timeZone });

    return startOfMonthLocal;
  }
  /**
   * @description
   * Return the date of the last day of the current month
   * @returns {string} local date string of the date
   * @example '31/01/2024 23:59:59'
   */
  getEndMonthDate() {
    let date = new Date();

    // Créez une nouvelle date avec le fuseau horaire local
    let endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59);

    // Obtenez le nom du fuseau horaire local
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Utilisez toLocaleString avec l'option timeZone
    const endOfMonthLocal = endOfMonth.toLocaleString(undefined, { timeZone });

    return endOfMonthLocal;
  }
  /**
   * @description
   * Return the date of the first day of the current trimester
   * @returns {string} local date string of the date
   * @example '01/01/2024 00:00:00'
   */
  getStartTrimesterDate(): string {
    const date = new Date();
    const startMonth = Math.floor(date.getMonth() / 3) * 3;

    const startOfTrimester = new Date(date.getFullYear(), startMonth, 1, 0, 0, 0, 0);

    return startOfTrimester.toLocaleString();
  }
  getEndTrimesterDate(): string {
    const date = new Date();
    const startMonth = Math.floor(date.getMonth() / 3) * 3;

    const endOfTrimester = new Date(date.getFullYear(), startMonth + 3, 0, 23, 59, 59, 999);

    return endOfTrimester.toLocaleString();
  }
  /**
   * @description
   * Return the date of the first day of the current year
   * @returns {string} local date string of the date
   * @example '01/01/2024 00:00:00'
   */
  getStartYearDate() {
    let date = new Date();
    // Créez une nouvelle date avec le fuseau horaire local
    let startOfYear = new Date(date.getFullYear(), 0, 1, 0, 0, 0);

    // Obtenez le nom du fuseau horaire local
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Utilisez toLocaleString avec l'option timeZone
    const startOfYearLocal = startOfYear.toLocaleString(undefined, { timeZone });

    return startOfYearLocal;
  }
  /**
   * @description
   * Return the date of the last day of the current year
   * @returns {string} local date string of the date
   * @example '31/12/2024 23:59:59'
   */
  getEndYearDate() {
    let date = new Date();
    // Créez une nouvelle date avec le fuseau horaire local
    let endOfYear = new Date(date.getFullYear(), 11, 31, 23, 59, 59);

    // Obtenez le nom du fuseau horaire local
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Utilisez toLocaleString avec l'option timeZone
    const endOfYearLocal = endOfYear.toLocaleString(undefined, { timeZone });

    return endOfYearLocal;
  }
  /**
   * @description
   * Return Formated datetime string
   * @param {string} dateTimeString lcoale date string of the date
   * @returns {string} Formated datetime string
   * @example '2020-01-01 00:00:00'
   */
  convertDateTime(dateTimeString: string) {
    const completeDate = dateTimeString.split(' ');
    const date = completeDate[0];
    const time = completeDate[1];
    const day = date.split('/')[0];
    const month = date.split('/')[1];
    const year = date.split('/')[2];
    const formattedDate = `${year}-${month}-${day}`;

    return `${formattedDate} ${time}`;
  }

  get1970Date() {
    const date = new Date(0);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const dateLocal = date.toLocaleString(undefined, { timeZone });
    return dateLocal;
  }

  getFormattedDateFromBibliolibDateTime(dateTime: DateTime) {
    const date = new Date(dateTime.date);
    date.setHours(+dateTime.hour.split(':')[0], +dateTime.hour.split(':')[1]);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const dateLocal = date.toLocaleString(undefined, { timeZone });

    return this.convertDateTime(dateLocal);
  }

  getMonthString(offset: number) {
    let date = new Date();
    let startOfMonth = new Date(date.getFullYear(), date.getMonth() + offset, 1, 0, 0, 0);

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const startOfMonthLocalStr = startOfMonth.toLocaleDateString('default', { timeZone, month: 'long' });

    return startOfMonthLocalStr;
  }

  getDateNow() {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
  }

  /**
   * Parses a date range string from query parameters and returns a TuiDayRange object.
   *
   * @param dateFromQueryParams - A string representing the date range in the format "startDate|endDate",
   * where each date is in the format "dd/MM/yyyy HH:mm:ss".
   * @returns A TuiDayRange object representing the parsed date range.
   */
  getFormattedTuiDayRange(dateFromQueryParams: string): TuiDayRange {
    const formattedStartDate: string = dateFromQueryParams.split('|')[0].split(' ')[0];
    const formattedEndDate: string = dateFromQueryParams.split('|')[1].split(' ')[0];

    return new TuiDayRange(
      new TuiDay(+formattedStartDate.split('/')[2], +formattedStartDate.split('/')[1] - 1, +formattedStartDate.split('/')[0]),
      new TuiDay(+formattedEndDate.split('/')[2], +formattedEndDate.split('/')[1] - 1, +formattedEndDate.split('/')[0]),
    );
  }

  /**
   *
   * @param dateRange
   * @returns A string representing the date range in the format "startDate|endDate",
   * where each date is in the format "dd/MM/yyyy HH:mm:ss".
   */
  getFormattedDateForQueryParamsFromTuiDateRange(dateRange: TuiDayRange): string {
    const startDate: TuiDay = dateRange.from;
    const endDate: TuiDay = dateRange.to;

    return `${startDate.day < 10 ? '0' + startDate.day : startDate.day}/${startDate.month < 9 ? '0' + (startDate.month + 1) : startDate.month + 1}/${startDate.year} 00:00:00|${endDate.day < 10 ? '0' + endDate.day : endDate.day}/${endDate.month < 9 ? '0' + (endDate.month + 1) : endDate.month + 1}/${endDate.year} 23:59:59`;
  }

  /**
   * Converts a date string into a `TuiDay` object.
   *
   * @param dateStr - The date string to be converted.
   * @returns A `TuiDay` object representing the date if the string is valid, or `null` if the string is invalid.
   */
  getTuiDayFromDateString(dateStr: string): TuiDay | null {
    const extractDate = dateStr.split(' ')[0];
    const [day, month, year] = extractDate.split('/').map(Number);

    if (!year || !month || !day) return null;
    return new TuiDay(year, month - 1, day);
  }

  /**
   * A mapping of human-readable date range labels to functions that return the corresponding start and end dates as string tuples.
   *
   * Each key represents a date range option (e.g., "Hier", "Le mois en cours") and maps to a function that computes the start and end dates for that range.
   * The returned tuple contains two ISO date strings: `[startDate, endDate]`.
   *
   * This mapping is typically used to populate date range selectors in the UI, allowing users to quickly choose common date ranges.
   */
  readonly mappingCommonDateRangesForSelect: Record<string, () => [string, string]> = {
    Hier: () => [this.getStartYesterdayDate(), this.getEndYesterdayDate()],
    'La semaine en cours': () => [this.getStartWeekDate(), this.getEndWeekDate()],
    "Le mois en cours à aujourd'hui": () => [this.getStartMonthDate(), this.getEndTodayDate()],
    'Le mois en cours': () => [this.getStartMonthDate(), this.getEndMonthDate()],
    [`${this.firstLetterCapitalizePipe.transform(this.getMonthString(-2))} à aujourd'hui`]: () => [
      this.getStartMonthWithOffsetDate(-2),
      this.getEndTodayDate(),
    ],
    'Le trimestre en cours': () => [this.getStartTrimesterDate(), this.getEndTrimesterDate()],
    "L'année en cours": () => [this.getStartYearDate(), this.getEndYearDate()],
    'Depuis le début': () => [this.get1970Date(), this.getEndTodayDate()],
  };

  /**
   * Returns a descriptive string representing a date range based on the provided start and end dates.
   * The method matches specific date ranges to predefined labels, such as "Hier" (Yesterday),
   * "La semaine en cours" (Current week), or "Depuis le début" (Since the beginning).
   * If no predefined range matches, it defaults to "Période personnalisée" (Custom period).
   *
   * @param date1 - The start date of the range in string format.
   * @param date2 - The end date of the range in string format.
   * @returns A string describing the date range, or an empty string if either date is invalid.
   */
  getFormattedDateRangeStringForSelect(date1: string, date2: string): string {
    if (!date1 || !date2) return '';

    if (date1 === this.getStartYesterdayDate() && date2 === this.getEndYesterdayDate()) {
      return 'Hier';
    }

    if (date1 === this.getStartWeekDate() && date2 === this.getEndWeekDate()) {
      return 'La semaine en cours';
    }

    if (date1 === this.getStartMonthDate() && date2 === this.getEndMonthDate()) {
      return 'Le mois en cours';
    }

    if (date1 === this.getStartMonthWithOffsetDate(-2) && date2 === this.getEndTodayDate()) {
      const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.substring(1);
      return `${capitalize(this.getMonthString(-2))} à aujourd'hui`;
    }

    if (date1 === this.getStartTrimesterDate() && date2 === this.getEndTrimesterDate()) {
      return 'Le trimestre en cours';
    }

    if (date1 === this.getStartYearDate() && date2 === this.getEndYearDate()) {
      return "L'année en cours";
    }

    if (date1 === this.get1970Date() && date2 === this.getEndTodayDate()) {
      return 'Depuis le début';
    }

    return 'Période personnalisée';
  }

  getMonthForSelect(startMonth: number, nbrMonth: number, selectYear: number) {
    const currentYear = new Date().getFullYear(); // Année actuelle
    const currentMonth = new Date().getMonth(); // Mois actuel (0-indexé)

    // Si ce n'est pas l'année en cours, afficher tous les mois de l'année
    if (selectYear !== currentYear) {
      nbrMonth = 12; // Total de mois dans une année
      startMonth = 0; // Départ depuis le premier mois
    } else {
      // Limiter les mois au mois actuel si on est dans l'année courante
      nbrMonth = currentMonth + 1; // +1 car getMonth() est 0-indexé
    }
    const template = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    let months = [];

    for (let i = 0; i < nbrMonth; i++) {
      const monthIndex = (startMonth + i) % 12;
      months.push({
        value: monthIndex + 1 < 10 ? `0${monthIndex + 1}` : monthIndex + 1,
        name: template[monthIndex],
      });
    }

    return months;
  }

  getCurrentMonthAndYear() {
    const date = new Date();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Mois de 01 à 12
    const year = date.getFullYear(); // Année actuelle
    return `${month}-${year}`;
  }
  getCurrentYear() {
    const date = new Date();
    const year = date.getFullYear(); // Année actuelle
    return `${year}`;
  }

  getYearFromDate(date: string | undefined): string {
    if (!date) return '-';
    const year = ('' + date).split('-')[0];
    return year || '-';
  }

  getYearFrom1960ForSelect(): string[] {
    const currentYear = new Date().getFullYear();
    const years: string[] = [];
    for (let year = currentYear; year >= 1960; year--) {
      years.push(year.toString());
    }
    return years;
  }
  /**
   * Convertit une date du format ISO (yyyy-MM-dd HH:mm:ss) vers le format français (dd/MM/yyyy HH:mm:ss)
   *
   * @param isoDate - Date au format "yyyy-MM-dd HH:mm:ss" (ex: "2025-12-08 00:00:00")
   * @returns Date au format "dd/MM/yyyy HH:mm:ss" (ex: "08/12/2025 00:00:00")
   */
  convertISODateToFrench(isoDate: string): string {
    if (!isoDate) return '';

    // Normalise: remplace 'T' par un espace, enlève millisecondes et offsets
    const normalized = isoDate
      .replace('T', ' ')
      .replace(/\.\d+/, '')
      .replace(/Z|[+-]\d{2}:\d{2}$/, '')
      .trim();

    const [datePart, timePart = '00:00:00'] = normalized.split(' ');

    const parts = datePart.split('-');
    if (parts.length < 3) return '';

    const [year, month, day] = parts;

    return `${day}/${month}/${year} ${timePart}`;
  }
  convertUTCDateToFrench(utilDate: string): string {
    if (!utilDate) return '';

    const isoUtc = utilDate.replace(' ', 'T') + 'Z';

    return new Date(isoUtc).toLocaleString('fr-FR', {
      timeZone: 'Europe/Paris',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  getMonthInLetters(monthNumber: number): string {
    const date = new Date(2000, monthNumber - 1, 1);
    return date.toLocaleString('fr-FR', { month: 'long' });
  }

  getFormatedDateForQueryParams(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    const second = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
  }
  /**
   * @description
   * Return the date of the first day and last day of the previous month
   * @returns {{ firstDay: string; lastDay: string }} Object containing the first and last day of the previous month
   * @example '{ firstDay: "2024-05-01", lastDay: "2024-05-31" }'
   */
  getPreviousMonthRange(): { firstDay: string; lastDay: string } {
    const now = new Date();

    const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);

    const format = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

    return {
      firstDay: format(firstDay),
      lastDay: format(lastDay),
    };
  }
}
